// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { BiteBalanceClient as ContractsClient } from "@bitebalance/contracts/client"
import {
  RecipeDetailResponseSchema,
  SUPPORTED_MEAL_SERVINGS,
  type RecipeDetailResponse,
} from "@bitebalance/contracts"
import { webGet, type WebRequestDeps } from "./webGet"

/**
 * Portion truth (owner decision 2026-09-04): GET /v1/recipes/{id}?servings=
 * <1|1.5|2> makes the SERVER scale the recipe to the user's planned portion
 * (`RecipeDetailResponse.portion`). The generated contracts client's
 * `getRecipe(id)` has no query parameter yet, so this small web-only wrapper
 * adds one over the same base URL / token / headers (webGet.ts) — no
 * client-side scaling, ever. `servings` outside SUPPORTED_MEAL_SERVINGS is
 * never sent (the base recipe is returned instead, with its own labels).
 */
export interface RecipeQuery {
  servings?: number
}

export type RecipePortionDeps = WebRequestDeps

export type RecipePortionClient = Omit<ContractsClient, "getRecipe"> & {
  getRecipe: (id: string, query?: RecipeQuery) => Promise<RecipeDetailResponse>
}

export function isSupportedMealServings(value: number): boolean {
  return (SUPPORTED_MEAL_SERVINGS as readonly number[]).includes(value)
}

export function withRecipePortion(client: ContractsClient, deps: RecipePortionDeps): RecipePortionClient {
  async function getRecipe(id: string, query?: RecipeQuery): Promise<RecipeDetailResponse> {
    const servings = query?.servings
    if (servings === undefined || !isSupportedMealServings(servings)) return client.getRecipe(id)
    return webGet(
      deps,
      `/v1/recipes/${encodeURIComponent(id)}`,
      { servings: String(servings) },
      RecipeDetailResponseSchema
    )
  }

  return { ...client, getRecipe }
}
